"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { Link, useRouter } from "@/i18n/navigation";
import { useCustomerAuth } from "@/context/auth-context";
import { AnnouncementBar } from "@/components/announcement-bar";
import { Navbar } from "@/components/navbar";
import { Footer } from "@/components/footer";
import { WhatsAppButton } from "@/components/whatsapp-button";
import { MobileTabBar } from "@/components/mobile-tab-bar";
import { useTranslations } from "next-intl";

const ease = [0.16, 1, 0.3, 1] as const;

function InputField({
  label,
  name,
  type = "text",
  value,
  onChange,
  required = false,
  placeholder,
  autoComplete,
}: {
  label: string;
  name: string;
  type?: string;
  value: string;
  onChange: (val: string) => void;
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <div>
      <label htmlFor={name} className="block text-xs font-medium text-text-secondary mb-1.5">
        {label} {required && <span className="text-negative">*</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="w-full h-11 px-3 bg-background border border-border text-sm text-foreground placeholder:text-text-tertiary outline-none focus:border-foreground transition-colors rounded-xl"
        style={{ transitionDuration: "var(--duration-fast)" }}
      />
    </div>
  );
}

export function LoginPage() {
  const { login, resendVerifyEmail } = useCustomerAuth();
  const router = useRouter();
  const t = useTranslations("account");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // EMAIL_NOT_VERIFIED is a distinct error code coming back from the
  // login proxy when the password matched but the account hasn't
  // clicked the verify link yet. The backend auto-resends the welcome
  // mail in that case, so we just need to surface a separate UI panel
  // that explains the situation + offers a manual "send again" button.
  const [verifyPendingEmail, setVerifyPendingEmail] = useState<string | null>(null);
  const [resendState, setResendState] = useState<"idle" | "sending" | "sent">("idle");

  const canSubmit = email.trim() && password.trim();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || submitting) return;

    setSubmitting(true);
    setError("");
    setVerifyPendingEmail(null);

    const result = await login(email, password);

    if (result.success) {
      router.push("/account");
    } else if (result.code === "EMAIL_NOT_VERIFIED") {
      setVerifyPendingEmail(email.trim());
      setResendState("sent");
      setSubmitting(false);
    } else {
      setError(result.error || t("loginFailed"));
      setSubmitting(false);
    }
  }

  async function handleResend() {
    if (!verifyPendingEmail || resendState === "sending") return;
    setResendState("sending");
    await resendVerifyEmail(verifyPendingEmail);
    setResendState("sent");
  }

  return (
    <>
      <AnnouncementBar />
      <Navbar />
      <main className="flex-1">
        <div className="content-wrapper py-12 lg:py-20">
          <nav className="flex items-center gap-1.5 text-xs text-text-tertiary mb-8">
            <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
            <span>/</span>
            <span className="text-text-secondary">{t("login")}</span>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease }}
            className="max-w-md mx-auto"
          >
            <div className="text-center mb-10">
              <p className="label-sm mb-3">{t("label")}</p>
              <h1 className="heading-md">{t("login")}</h1>
            </div>

            {verifyPendingEmail && (
              <div className="mb-6 p-4 bg-cta/5 border border-cta/20 rounded-xl text-sm space-y-3">
                <p className="font-medium text-foreground">
                  {t("verifyPendingTitle")}
                </p>
                <p className="text-text-secondary">
                  {t("verifyPendingBody", { email: verifyPendingEmail })}
                </p>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendState === "sending"}
                  className="inline-flex items-center justify-center px-4 h-10 bg-foreground text-background text-[13px] font-medium rounded-lg hover:bg-foreground/85 disabled:opacity-60"
                >
                  {resendState === "sent"
                    ? t("verifyResendSent")
                    : resendState === "sending"
                      ? t("verifyResending")
                      : t("verifyResendCta")}
                </button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <InputField
                label={t("emailField")}
                name="email"
                type="email"
                value={email}
                onChange={setEmail}
                required
                placeholder="je@email.nl"
                autoComplete="email"
              />

              <InputField
                label={t("passwordField")}
                name="password"
                type="password"
                value={password}
                onChange={setPassword}
                required
                placeholder={t("passwordPlaceholder")}
                autoComplete="current-password"
              />

              {error && (
                <div className="p-3 bg-negative/5 border border-negative/20 text-sm text-negative rounded-xl space-y-1.5">
                  <p>{error}</p>
                  {/* Imported-customer hint: ~99% of Unity's customer base
                      came from the Lightspeed migration with no password.
                      The wachtwoord-vergeten flow auto-detects this and
                      sends a friendly "claim your account" email — and
                      the backend ALSO fires that email automatically on
                      a failed login (PR #319). Either path lands them
                      back in their account with order history intact. */}
                  <p className="text-text-tertiary">
                    {t("loginFailedMigrationHint")}{" "}
                    <Link
                      href="/account/wachtwoord-vergeten"
                      className="text-foreground underline hover:no-underline"
                    >
                      {t("forgotPassword")}
                    </Link>
                    .
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={!canSubmit || submitting}
                className="w-full h-12 bg-foreground text-background label-sm hover:bg-foreground/85 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 rounded-lg"
                style={{ transitionDuration: "var(--duration-fast)", transitionTimingFunction: "var(--ease-out)" }}
              >
                {submitting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                    </svg>
                    {t("pleaseWait")}
                  </>
                ) : (
                  t("loginButton")
                )}
              </button>
            </form>

            {/* Links */}
            <div className="mt-8 pt-8 border-t border-border space-y-4 text-center">
              <p className="text-sm text-text-secondary">
                {t("noAccount")}{" "}
                <Link
                  href="/account/registreren"
                  className="text-foreground font-medium hover:underline"
                >
                  {t("createAccount")}
                </Link>
              </p>
              <p className="text-sm text-text-tertiary">
                <Link
                  href="/account/wachtwoord-vergeten"
                  className="hover:text-foreground transition-colors"
                  style={{ transitionDuration: "var(--duration-fast)" }}
                >
                  {t("forgotPassword")}
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
      <WhatsAppButton />
      <MobileTabBar />
    </>
  );
}
